import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

const targetText = 'price-discount';
const autocompleteOptions = [
  'export-as-pdf',
  'killswitch',
  'price-discount',
  'landing-page-layout',
];

export function CodeAutocomplete({ className }: { className?: string }) {
  const [typedText, setTypedText] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [animationTrigger, setAnimationTrigger] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const textSpanRef = useRef<HTMLSpanElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    let typingInterval: NodeJS.Timeout;
    let selectionInterval: NodeJS.Timeout;
    let completionTimeout: NodeJS.Timeout;
    let resetTimeout: NodeJS.Timeout;

    const startAnimation = () => {
      setTypedText('');
      setShowAutocomplete(false);
      setHighlightedIndex(null);
      setIsCompleted(false);

      const initialTypingText = 'price';
      let i = 0;
      typingInterval = setInterval(() => {
        if (i < initialTypingText.length) {
          setTypedText(initialTypingText.substring(0, i + 1));
          i++;
        } else {
          clearInterval(typingInterval);
          startSelection();
        }
      }, 120);
    };

    const startSelection = () => {
      let currentHighlight = 0;
      setHighlightedIndex(0);

      selectionInterval = setInterval(() => {
        currentHighlight++;
        if (currentHighlight <= 2) {
          setHighlightedIndex(currentHighlight);
        } else {
          clearInterval(selectionInterval);
          completeText();
        }
      }, 400);
    };

    const completeText = () => {
      completionTimeout = setTimeout(() => {
        setTypedText(targetText);
        setIsCompleted(true);

        resetTimeout = setTimeout(() => {
          setAnimationTrigger((v) => v + 1);
        }, 3000);
      }, 250);
    };

    startAnimation();

    return () => {
      clearInterval(typingInterval);
      clearInterval(selectionInterval);
      clearTimeout(completionTimeout);
      clearTimeout(resetTimeout);
    };
  }, [isClient, animationTrigger]);

  useEffect(() => {
    // Show autocomplete suggestions after 3 characters are typed, and hide it on completion.
    if (typedText.length >= 3 && !isCompleted) {
      setShowAutocomplete(true);
    } else {
      setShowAutocomplete(false);
    }
  }, [typedText, isCompleted]);

  useLayoutEffect(() => {
    if (showAutocomplete && textSpanRef.current && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const textSpan = textSpanRef.current;
      const pre = textSpan.closest('pre');
      if (!pre) return;
      const textRect = textSpan.getBoundingClientRect();
      const preRect = pre.getBoundingClientRect();
      const dropdownWidth = 208;
      if (textRect.left + dropdownWidth > preRect.right) {
        dropdown.style.left = 'auto';
        dropdown.style.right = '0px';
      } else {
        dropdown.style.left = '0px';
        dropdown.style.right = 'auto';
      }
    }
  }, [showAutocomplete, typedText]);

  const isTyping = typedText.length < targetText.length && typedText.length > 0;

  return (
    <div
      className={cn(
        'bg-background p-4 border select-none rounded-lg',
        className,
      )}
    >
      <div className="relative text-gray-300 w-full">
        <pre>
          <code
            className={cn(
              'whitespace-pre-wrap break-words text-xs md:text-sm',
              'font-mono',
            )}
          >
            <span className="text-[#569CD6]/50">function</span>&nbsp;
            <span className="text-[#DCDCAA]/50">MyComponent</span>
            <span className="text-foreground/50">() {'{'}</span>
            <br />
            <span className="text-[#569CD6]/50 pl-4">const</span>&nbsp;
            <span className="text-foreground/50">{'{'}</span>&nbsp;
            <span className="text-[#9CDCFE]/50">value</span>,&nbsp;
            <span className="text-[#9CDCFE]/50">isReady</span>&nbsp;
            <span className="text-foreground/50">{'}'} =</span>&nbsp;
            <span className="text-[#DCDCAA]">useFlag</span>(
            <span className="text-[#CE9178]">&#39;</span>
            <span className="relative inline-block" ref={textSpanRef}>
              <span className="text-[#CE9178]">{typedText}</span>
              {isTyping && (
                <span className="inline-block w-0.5 h-5 bg-white ml-px -mb-1 animate-pulse" />
              )}
              <AnimatePresence>
                {showAutocomplete && (
                  <motion.div
                    ref={dropdownRef}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 5 }}
                    exit={{ opacity: 0, y: 15 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="absolute top-full mt-2 bg-[#252526] border border-[#3c3c3c] rounded-md shadow-2xl w-52 z-10 overflow-hidden"
                  >
                    <ul>
                      {autocompleteOptions.map((option, index) => (
                        <li
                          key={option}
                          className={`px-3 py-1.5 text-left text-sm flex items-center gap-2.5 ${
                            index === highlightedIndex
                              ? 'bg-[#094771] text-white'
                              : 'text-gray-300 hover:bg-[#2A2D2E]'
                          }`}
                        >
                          <span className="text-purple-400 text-xs">◆</span>
                          {option}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </span>
            <span className="text-[#CE9178]">&#39;</span>);
            <br />
            <br />
            <span className="text-[#C586C0]/50 pl-4">if</span>&nbsp;
            <span className="text-foreground/50">(!</span>
            <span className="text-[#9CDCFE]/50">isReady</span>
            <span className="text-foreground/50">)</span>&nbsp;
            <span className="text-foreground/50">{'{'}</span>
            <br />
            <span className="text-[#C586C0]/50 pl-8">return</span>&nbsp;
            <span className="text-[#4EC9B0]/50">{'<div>'}</span>
            <span className="text-foreground/50">Loading...</span>
            <span className="text-[#4EC9B0]/50">{'</div>'}</span>
            <br />
            <span className="text-foreground/50 pl-4">{'}'}</span>
            <br />
            <br />
            <span className="text-[#C586C0]/50 pl-4">return</span>&nbsp;
            <span className="text-foreground/50">(</span>
            <br />
            <span className="text-[#4EC9B0]/50 pl-8">{'<div>'}</span>
            <span className="text-gray-300/50">Discount is </span>
            <span className="text-foreground/50">{'{'}</span>
            <span className="text-gray-300/50"> value </span>
            <span className="text-foreground/50">{'}'}</span>
            <span className="text-[#4EC9B0]/50">{'</div>'}</span>
            <br />
            <span className="text-foreground/50 pl-4">{')'}</span>
            <br />
            <span className="text-foreground/50">{'}'}</span>
          </code>
        </pre>
      </div>
    </div>
  );
}
